export type Message = {
  message?: string;
  data?: any;
  correlationId?: string;
};

export interface MessageEvent {
  data?: any;
  source?: any;
}

// Publisher pages that may embed this iframe. This mirrors the publisher origins in the
// interactive-api CORS allowlist (services/interactive-api/main.go) -- an origin absent from that
// list cannot use the API anyway, so the two must be updated together when a brand domain is added.
const TRUSTED_PARENT_ORIGINS = [
  "https://www.welt.de",
  "https://ssl.welt.de",
  "https://www.bild.de",
  "https://m.bild.de",
  "https://interactive-admin.la.welt.de",
  "https://interactive-admin.staging.la.welt.de",
  // ESD-2418 staging autofill test rail (iat.staging.la.bild.de/lab). Without this the widget
  // refuses to postMessage with that parent, so `getParticipationToken` is never sent and the
  // registration smart flow can never authenticate — it silently falls back to the manual form.
  // Staging only and VPN-fenced; production journeys run on www.bild.de, already listed above.
  "https://iat.staging.la.bild.de",
  // ESD-2418 in-app wrapper. A mobile-app webview opens the widget standalone; ApplicationLoader
  // redirects it to the iat.la.bild.de/embed hyper wrapper, which re-embeds the widget with THIS as
  // the parent origin. Without it the widget refuses to postMessage that parent, so
  // `getParticipationToken` is never sent and autofill falls back to the manual form in-app. Paired
  // with the same origin in interactive-api config.AllowedOrigins() (production): the session endpoint
  // enforces Origin server-side, so hyper's mint from this page is refused without it.
  "https://iat.la.bild.de",
  "https://local.la.welt.de:8556",
  "https://local.la.welt.de:8555",
  "https://local.la.welt.de:8452",
  "https://local-amp.la.welt.de:8555",
  "https://local.la.spring-media.de:8655",
];

export default class PostMessageService {
  private receiverWindow: Window;

  private messageSubscriber: {
    [key: string]: (
      data: any,
      sourceFrame: Window | HTMLIFrameElement,
      event: MessageEvent
    ) => void;
  } = {};

  public constructor(receiverWindow?: Window) {
    if (receiverWindow) {
      this.receiverWindow = receiverWindow;
    }
    this.handlePostMessage = this.handlePostMessage.bind(this);
    this.initPostMessageListener();
  }

  public setReceiver(receiverWindow: Window): void {
    this.receiverWindow = receiverWindow;
  }

  // The embedding page's origin, taken from ancestorOrigins where available and otherwise from the
  // referrer, and only accepted when it is a known publisher. Returns undefined when it cannot be
  // established, which is what stops a response-bearing message from being sent at all.
  private trustedParentOrigin(): string | undefined {
    const candidates: string[] = [];
    const ancestors = (window.location as any).ancestorOrigins;
    if (ancestors && ancestors.length > 0) {
      candidates.push(ancestors[0]);
    }
    if (document.referrer) {
      try {
        candidates.push(new URL(document.referrer).origin);
      } catch {
        // malformed referrer - ignore
      }
    }
    return candidates.find((origin) => TRUSTED_PARENT_ORIGINS.includes(origin));
  }

  public listener<T>(
    message: string,
    handler: (data: T, sourceFrame: Window | HTMLIFrameElement) => any
  ): void {
    this.messageSubscriber[message] = (data, sourceFrame) =>
      handler(data, sourceFrame);
  }

  // The embed's layout height. Broadcast with "*" rather than addressed to a known publisher,
  // because the embed is created with scrolling="no" (packages/hyper AppLoader): it cannot scroll
  // its own content, so this message is the only thing that lets it grow past its initial 800px.
  // Withholding it from an embedder we cannot name -- a native app webview, a stripped referrer,
  // a browser without location.ancestorOrigins -- leaves the widget clipped with no way to reach
  // what is below, which stranded readers part-way through the registration form.
  //
  // The accepted tradeoff: whoever embeds this iframe learns one number, the child document's
  // layout height. That is a real disclosure -- a cross-origin parent can read the height it
  // assigned to the iframe element, but not the child's scrollHeight -- and it is judged
  // acceptable against a registration form the reader cannot scroll or submit. Everything
  // carrying a nonce, user data or tracking stays on the allowlisted send() path.
  public sendResize(height: number): void {
    this.receiverWindow.postMessage({ message: "resize", data: height }, "*");
  }

  public sendAmpResize(height: number): void {
    this.receiverWindow.postMessage(
      {
        sentinel: "amp",
        type: "embed-size",
        height: height,
      },
      // The AMP embed-size protocol requires "*": the AMP runtime host is not knowable here. The
      // payload is a layout height only -- no nonce, no user data.
      "*"
    );
  }

  public sendAppResize(height: number): void {
    // Event handle "needsRefresh" is defined here
    // https://github.com/spring-media/apps-ios-news/blob/e9eeddfd341d865090d9c99bdd77bc7d7ad21bf9/Widgetizr%20Sandbox/LeanWidgets
    // /WebView/WebViewJanusWidgetCell.swift#L264
    (window as any).webkit?.messageHandlers?.needsRefresh?.postMessage({
      documentHeight: height,
      documentOffsetHeight: height,
    });
  }

  public send<T>(
    message: string | Message,
    answerCallback?: (data: T, sourceFrame: Window | HTMLIFrameElement) => void
  ): any {
    if (!this.receiverWindow) {
      throw new Error("Missing receiver window");
    }

    let outgoingMessage = message;
    if (answerCallback) {
      const messageName =
        typeof message === "string" ? message : message.message;
      const responseMessageName = `${messageName}_response`;
      const correlationId = this.createCorrelationId();
      this.messageSubscriber[responseMessageName] = (
        data,
        sourceFrame,
        event
      ) => {
        if (event.data?.correlationId !== correlationId) {
          return;
        }
        delete this.messageSubscriber[responseMessageName];
        answerCallback(data, sourceFrame);
      };
      outgoingMessage =
        typeof message === "string"
          ? { message, correlationId }
          : { ...message, correlationId };
    }

    // Every message is addressed to one known publisher origin -- never "*". Response-bearing
    // messages carry the correlationId nonce that authenticates the reply, and the fire-and-forget
    // ones carry tracking and Permutive audience data, so neither may be handed to an arbitrary
    // embedder. An unrecognised embedder therefore receives nothing, which is the intended
    // behaviour: a page that is not an authorised publisher should not be served this iframe's data.
    const parentOrigin = this.trustedParentOrigin();
    if (!parentOrigin) {
      return;
    }
    this.receiverWindow.postMessage(outgoingMessage, parentOrigin);
  }

  private initPostMessageListener(): void {
    window.addEventListener("message", this.handlePostMessage);
  }

  private handlePostMessage(event: MessageEvent): any {
    if (event.source !== this.receiverWindow) {
      return;
    }

    const { message, data, correlationId } = event.data ?? {};
    if (!message || !this.messageSubscriber[message]) {
      return;
    }

    const iFrame = this.findRealIframe(event.source);
    // IFrame not found: The direction of the communication is top window to iFrame
    // Use the Window object event.source as sourceFrame
    const sourceFrame = iFrame ? iFrame : event.source;
    const response = this.messageSubscriber[message](data, sourceFrame, event);

    if (response as any) {
      // The reply echoes the correlationId, so it is addressed to the known publisher rather than
      // broadcast. event.source was already checked to be the window we message.
      const parentOrigin = this.trustedParentOrigin();
      if (!parentOrigin) {
        return;
      }
      event.source.postMessage(
        {
          message: `${message}_response`,
          data: response,
          correlationId,
        },
        parentOrigin
      );
    }
  }

  private createCorrelationId(): string {
    const cryptoWithRandomUuid = window.crypto as Crypto & {
      randomUUID?: () => string;
    };
    return (
      cryptoWithRandomUuid?.randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    );
  }

  private findRealIframe(sourceWindow: Window): HTMLIFrameElement | undefined {
    const iframes = document.getElementsByTagName("iframe");
    for (let i = 0; i < iframes.length; i++) {
      if (iframes[i].contentWindow === sourceWindow) {
        return iframes[i];
      }
    }
  }
}
